// frontend/src/App.js
// Main application entry point — sets up providers, theme, notifications and routes.

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
// Ant Design core app, theme and notification utilities
import { App as AntApp, ConfigProvider, message, Spin } from 'antd';
// Authentication context to manage user sessions
import { AuthProvider, useAuth } from './context/AuthContext';
import { useTranslation } from 'react-i18next';

import './styles/table.css';
import './styles/card.css';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import AppUpdateNotification from './components/AppUpdateNotification';

import MainLayout from './components/MainLayout';
import ImpersonationBanner from './components/ImpersonationBanner';
import AdminLayout from './layouts/AdminLayout';
import TeacherLayout from './layouts/TeacherLayout';
import MentorLayout from './layouts/MentorLayout';

import { ThemeProvider, useTheme } from './context/ThemeContext';
import { SettingsProvider } from './context/SettingsContext';
import { AcademicYearProvider } from './context/AcademicYearContext';
import { NotificationProvider } from './context/NotificationContext';

// Import Modular Routes
import StudentRoutes from './routes/StudentRoutes';
import LibraryRoutes from './routes/LibraryRoutes';
import KgRoutes from './routes/KgRoutes';
import ReportRoutes from './routes/ReportRoutes';
import AdminRoutes from './routes/AdminRoutes';
import HRRoutes from './routes/HRRoutes';
import AssignmentRoutes from './routes/AssignmentRoutes';
import AcademicYearRoutes from './routes/AcademicYearRoutes';
import FeeRoutes from './routes/FeeRoutes';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
// Students related pages removed (handled in StudentRoutes)

const Attendance = lazy(() => import('./pages/AttendancePage'));
const MarksPage = lazy(() => import('./pages/MarksPage'));
const ClassManagementPage = lazy(() => import('./pages/ClassManagementPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
// StudentProfilePage handled in StudentRoutes
const ClassSubjectsPage = lazy(() => import('./pages/ClassSubjectsPage'));
const SubjectManagementPage = lazy(() => import('./pages/SubjectManagementPage'));
const ExamManagementPage = lazy(() => import('./pages/ExamManagementPage'));
const ExamTypeManagementPage = lazy(() => import('./pages/ExamTypeManagementPage'));
const GradeManagementPage = lazy(() => import('./pages/GradeManagementPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
// StudentFormPage handled in StudentRoutes
const AdmissionPage = lazy(() => import('./pages/AdmissionPage'));
const InquiryListPage = lazy(() => import('./pages/InquiryListPage'));
const ReceptionPage = lazy(() => import('./pages/ReceptionPage'));
const AlumniPage = lazy(() => import('./pages/AlumniPage'));
const FormBuilderPage = lazy(() => import('./pages/admin/FormBuilderPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const PromotionPathsPage = lazy(() => import('./pages/PromotionPathsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SingleClassManagementPage = lazy(() => import('./pages/SingleClassManagementPage'));
// ReportsPage and report sub-pages removed (handled in ReportRoutes)
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
// SystemControl removed (handled in AdminRoutes)
const LicensePage = lazy(() => import('./pages/LicensePage'));
const TimetablePage = lazy(() => import('./pages/TimetablePage'));
// Library pages removed (handled in LibraryRoutes)
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const HostelDashboard = lazy(() => import('./pages/hostel/HostelDashboard'));
const LaundryDashboard = lazy(() => import('./pages/laundry/LaundryDashboard'));
const HifzDashboard = lazy(() => import('./pages/hifz/HifzDashboard'));
const DiaryPage = lazy(() => import('./pages/DiaryPage'));
const NoticesPage = lazy(() => import('./pages/NoticesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const TransportPage = lazy(() => import('./pages/TransportPage'));
const CommunicationPage = lazy(() => import('./pages/CommunicationPage'));
const EmailIntegration = lazy(() => import('./pages/admin/EmailIntegration'));
const SMSIntegration = lazy(() => import('./pages/admin/SMSIntegration'));
const WhatsAppSetup = lazy(() => import('./pages/admin/WhatsAppSetup'));

// KG pages removed (handled in KgRoutes)

// Configure global message behavior (Ant Design notifications)
message.config({
  maxCount: 3,
  duration: 3,
});

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

// Wrapper component to consume ThemeContext
const AppContent = () => {
  const { themeConfig } = useTheme();
  const { i18n } = useTranslation();

  const direction = i18n.dir(i18n.language);

  const RoleLayoutWrapper = ({ children }) => {
    const { user } = useAuth();

    let layout;
    if (!user) {
      layout = <MainLayout>{children}</MainLayout>;
    } else if (user.role === 'super_admin' || user.role === 'principal' || user.role === 'developer') {
      layout = <AdminLayout user={user}>{children}</AdminLayout>;
    } else if (user.role === 'teacher') {
      layout = <TeacherLayout user={user}>{children}</TeacherLayout>;
    } else if (user.role === 'mentor' || user.role === 'tutor') {
      layout = <MentorLayout user={user}>{children}</MentorLayout>;
    } else {
      // Fallback for students, parents, library staff, etc.
      layout = <MainLayout>{children}</MainLayout>;
    }

    return (
      <>
        <ImpersonationBanner />
        {layout}
      </>
    );
  };

  return (
    <ConfigProvider theme={themeConfig} direction={direction}>
      <AntApp>
        <NotificationProvider>
          {/* Small helper to expose PWA install prompt when available */}
          <AppUpdateNotification />
          <Router>
            <ScrollToTop />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public Routes: accessible without authentication */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/admission" element={<AdmissionPage />} />
                <Route path="/maintenance" element={<MaintenancePage />} />

                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                } />

                {/* Protected Routes: requires authenticated user. */}
                <Route path="/admin/forms" element={
                  <ProtectedRoute>
                    <FormBuilderPage />
                  </ProtectedRoute>
                } />

                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <RoleLayoutWrapper>
                        <Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>}>
                          <Routes>
                            {/* Dashboard — landing page for logged-in users */}
                            <Route path="/" element={<Dashboard />} />

                            {/* Modular Routes */}
                            <Route path="/students/*" element={<StudentRoutes />} />
                            <Route path="/library/*" element={<LibraryRoutes />} />
                            <Route path="/kg/*" element={<KgRoutes />} />
                            <Route path="/reports/*" element={<ReportRoutes />} />
                            <Route path="/admin/*" element={<AdminRoutes />} />
                            <Route path="/hr/*" element={<HRRoutes />} />

                            {/* Remaining Top-Level Routes */}
                            <Route path="/attendance" element={<Attendance />} />
                            <Route path="/marks" element={<MarksPage />} />
                            <Route path="/classes" element={<ClassManagementPage />} />
                            <Route path="/users" element={<UserManagementPage />} />
                            <Route path="/assignments/*" element={<AssignmentRoutes />} />
                            <Route path="/class-subjects" element={<ClassSubjectsPage />} />
                            <Route path="/subjects" element={<SubjectManagementPage />} />
                            <Route path="/exams" element={<ExamManagementPage />} />
                            <Route path="/exam-types" element={<ExamTypeManagementPage />} />
                            <Route path="/grades" element={<GradeManagementPage />} />
                            <Route path="/calendar" element={<CalendarPage />} />
                            <Route path="/academic-years/*" element={<AcademicYearRoutes />} />
                            <Route path="/inquiries" element={<InquiryListPage />} />
                            <Route path="/reception" element={<ReceptionPage />} />
                            <Route path="/alumni" element={<AlumniPage />} />
                            <Route path="/logs" element={<AuditLogPage />} />
                            <Route path="/promotion-paths" element={<PromotionPathsPage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/settings" element={<SettingsPage />} />
                            <Route path="/settings/license" element={<LicensePage />} />
                            <Route path="/settings/whatsapp" element={<WhatsAppSetup />} />
                            <Route path="/fees/*" element={<FeeRoutes />} />
                            <Route path="/timetable" element={<TimetablePage />} />
                            <Route path="/classes/:id" element={<SingleClassManagementPage />} />

                            <Route path="/hostel" element={<HostelDashboard />} />
                            <Route path="/laundry" element={<LaundryDashboard />} />
                            <Route path="/hifz" element={<HifzDashboard />} />
                            <Route path="/diary" element={<DiaryPage />} />
                            <Route path="/notices" element={<NoticesPage />} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                            <Route path="/communication" element={<CommunicationPage />} />
                            <Route path="/communication/email" element={<EmailIntegration />} />
                            <Route path="/communication/sms" element={<SMSIntegration />} />
                            <Route path="/transport" element={<TransportPage />} />
                            <Route path="/transport/vehicles" element={<TransportPage />} />
                            <Route path="/transport/routes" element={<TransportPage />} />
                            <Route path="/transport/assignments" element={<TransportPage />} />

                          </Routes>
                        </Suspense>
                      </RoleLayoutWrapper>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </Router>
        </NotificationProvider>
      </AntApp>
    </ConfigProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <AcademicYearProvider>
        <SettingsProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </SettingsProvider>
      </AcademicYearProvider>
    </AuthProvider>
  );
}

export default App;
